import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../game/constants';
import { AssetPackManager } from '../lib/assets';

/**
 * AssetLabScene - F12-toggle dev overlay for runtime asset inspection
 *
 * Features:
 * - Search texture keys / frame names
 * - Atlas frame viewer with navigation
 * - Animation preview by key with FPS control
 * - Loaded pack list + last rebuild revision
 * - Pixel art / smoothing toggles
 * - Audio panel: loaded keys, current BGM, play/stop controls
 *
 * This is READ-ONLY - no editing, no filesystem writes.
 */

type Tab = 'textures' | 'animations' | 'audio' | 'packs';

export class AssetLabScene extends Phaser.Scene {
  // Texture browser state
  private currentAtlasIndex = 0;
  private currentFrameIndex = 0;
  private atlases: string[] = [];
  private filteredAtlases: string[] = [];
  private frames: string[] = [];
  private filteredFrames: string[] = [];
  private previewSprite?: Phaser.GameObjects.Sprite;
  private infoText?: Phaser.GameObjects.Text;

  // Animation preview state
  private animKeys: string[] = [];
  private currentAnimIndex = 0;
  private animSprite?: Phaser.GameObjects.Sprite;
  private animFps = 12;
  private isAnimPlaying = false;

  // Audio state
  private audioKeys: string[] = [];
  private currentAudioIndex = 0;
  private currentBgm?: Phaser.Sound.BaseSound;
  private currentBgmKey?: string;

  // UI state
  private currentTab: Tab = 'textures';
  private pixelArtMode = true;
  private tabButtons: Map<Tab, Phaser.GameObjects.Text> = new Map();
  private contentContainer?: Phaser.GameObjects.Container;

  // Pack state
  private buildRevision: string | null = null;

  constructor() {
    super({ key: 'AssetLabScene' });
  }

  create(): void {
    // Dark overlay background
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x1a1a2e, 0.95).setOrigin(0);

    // Header
    this.add.rectangle(0, 0, GAME_WIDTH, 40, 0x16213e).setOrigin(0);
    this.add.text(10, 10, '🔬 Asset Lab', { font: 'bold 16px Arial', color: '#e94560' });

    // Build revision
    this.buildRevision = AssetPackManager.getBuildRevision();
    this.add.text(GAME_WIDTH - 10, 10, `Rev: ${this.buildRevision || 'unknown'}`, {
      font: '12px monospace',
      color: '#666666',
    }).setOrigin(1, 0);

    // Tab bar
    this.createTabBar();

    // Content container
    this.contentContainer = this.add.container(0, 80);

    // Gather data
    this.gatherAssets();

    // Initial view
    this.switchTab('textures');

    // Input handlers
    this.setupInputHandlers();

    // Help text
    this.add.text(10, GAME_HEIGHT - 25, 'ESC: Close | TAB: Switch tabs | P: Toggle pixel art | ←→: Navigate | SPACE: Play/Stop', {
      font: '11px monospace',
      color: '#666666',
    });
  }

  private createTabBar(): void {
    const tabs: { key: Tab; label: string }[] = [
      { key: 'textures', label: '🖼️ Textures' },
      { key: 'animations', label: '🎬 Animations' },
      { key: 'audio', label: '🔊 Audio' },
      { key: 'packs', label: '📦 Packs' },
    ];

    let x = 10;
    for (const tab of tabs) {
      const btn = this.add.text(x, 50, tab.label, {
        font: '13px Arial',
        color: '#aaaaaa',
        backgroundColor: '#0f3460',
        padding: { x: 12, y: 6 },
      });
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => this.switchTab(tab.key));
      btn.on('pointerover', () => btn.setColor('#ffffff'));
      btn.on('pointerout', () => btn.setColor(this.currentTab === tab.key ? '#e94560' : '#aaaaaa'));
      this.tabButtons.set(tab.key, btn);
      x += btn.width + 8;
    }
  }

  private switchTab(tab: Tab): void {
    this.currentTab = tab;

    // Update tab button styles
    for (const [key, btn] of this.tabButtons) {
      btn.setColor(key === tab ? '#e94560' : '#aaaaaa');
    }

    // Clear content
    this.contentContainer?.removeAll(true);

    // Render new content
    switch (tab) {
      case 'textures':
        this.renderTexturesTab();
        break;
      case 'animations':
        this.renderAnimationsTab();
        break;
      case 'audio':
        this.renderAudioTab();
        break;
      case 'packs':
        this.renderPacksTab();
        break;
    }
  }

  private gatherAssets(): void {
    // Gather texture atlases
    this.atlases = Object.keys(this.textures.list || {})
      .filter((key) => key !== '__DEFAULT' && key !== '__MISSING' && key !== '__WHITE')
      .sort();
    this.filteredAtlases = [...this.atlases];

    // Gather animation keys from the global anims manager
     
    this.animKeys = Object.keys((this.anims as any).anims.entries).sort();

    // Gather audio keys
    this.audioKeys = [];
    if (this.cache.audio) {
       
      const entries = (this.cache.audio as any).entries as Map<string, unknown>;
      entries.forEach((_value, key) => {
        this.audioKeys.push(key);
      });
    }
    this.audioKeys.sort();

    // Get current BGM if playing
     
    const soundMgr = this.sound as any;
    if (soundMgr.sounds) {
      for (const sound of soundMgr.sounds) {
        if (sound.isPlaying) {
          this.currentBgmKey = sound.key;
          this.currentBgm = sound;
          break;
        }
      }
    }
  }

  private renderTexturesTab(): void {
    const container = this.contentContainer!;

    // Search box label
    container.add(this.add.text(10, 0, 'Search:', { font: '12px Arial', color: '#888888' }));

    // Info display
    this.infoText = this.add.text(10, 30, '', { font: '13px monospace', color: '#ffffff' });
    container.add(this.infoText);

    // Preview area
    if (this.filteredAtlases.length > 0) {
      this.selectAtlas(0);
    } else {
      this.infoText.setText('No textures loaded');
    }

    // Pixel art toggle indicator
    const pixelText = this.add.text(GAME_WIDTH - 10, 0, `Pixel Art: ${this.pixelArtMode ? 'ON' : 'OFF'}`, {
      font: '12px Arial',
      color: this.pixelArtMode ? '#4ade80' : '#ef4444',
    }).setOrigin(1, 0);
    container.add(pixelText);
  }

  private renderAnimationsTab(): void {
    const container = this.contentContainer!;

    if (this.animKeys.length === 0) {
      container.add(this.add.text(10, 10, 'No animations defined', { font: '14px Arial', color: '#888888' }));
      return;
    }

    // Animation info
    const infoText = this.add.text(10, 0, '', { font: '13px monospace', color: '#ffffff' });
    container.add(infoText);

    // FPS control
    container.add(this.add.text(10, GAME_HEIGHT - 150, `FPS: ${this.animFps}  (↑/↓ to adjust)`, {
      font: '12px Arial',
      color: '#888888',
    }));

    // Play status
    const playStatus = this.add.text(10, GAME_HEIGHT - 130, this.isAnimPlaying ? '▶ Playing' : '⏸ Paused', {
      font: '12px Arial',
      color: this.isAnimPlaying ? '#4ade80' : '#fbbf24',
    });
    container.add(playStatus);

    // Create preview sprite
    this.updateAnimPreview(infoText);
  }

  private renderAudioTab(): void {
    const container = this.contentContainer!;

    // Current BGM display
    container.add(this.add.text(10, 0, `Current BGM: ${this.currentBgmKey || 'None'}`, {
      font: '14px Arial',
      color: '#e94560',
    }));

    // Audio list
    container.add(this.add.text(10, 30, `Loaded audio keys (${this.audioKeys.length}):`, {
      font: '12px Arial',
      color: '#888888',
    }));

    // Scrollable list (simplified - just show first 15)
    const visible = this.audioKeys.slice(0, 15);
    let y = 55;
    for (let i = 0; i < visible.length; i++) {
      const key = visible[i];
      const isPlaying = this.currentBgmKey === key;
      const text = this.add.text(20, y, `${isPlaying ? '▶' : '○'} ${key}`, {
        font: '12px monospace',
        color: isPlaying ? '#4ade80' : '#cccccc',
      });
      text.setInteractive({ useHandCursor: true });
      text.on('pointerdown', () => this.playAudio(key));
      text.on('pointerover', () => text.setColor('#ffffff'));
      text.on('pointerout', () => text.setColor(isPlaying ? '#4ade80' : '#cccccc'));
      container.add(text);
      y += 18;
    }

    if (this.audioKeys.length > 15) {
      container.add(this.add.text(20, y, `... and ${this.audioKeys.length - 15} more`, {
        font: '11px Arial',
        color: '#666666',
      }));
    }

    // Controls hint
    container.add(this.add.text(10, GAME_HEIGHT - 130, 'SPACE: Play/Stop | ←→: Navigate', {
      font: '11px Arial',
      color: '#666666',
    }));
  }

  private renderPacksTab(): void {
    const container = this.contentContainer!;

    // Build info
    container.add(this.add.text(10, 0, `Build Revision: ${this.buildRevision || 'Not compiled'}`, {
      font: '14px Arial',
      color: '#e94560',
    }));

    // Loaded packs
    const loadedPacks = AssetPackManager.getLoadedPacks();
    container.add(this.add.text(10, 30, `Loaded Packs (${loadedPacks.length}):`, {
      font: '12px Arial',
      color: '#888888',
    }));

    let y = 55;
    for (const packId of loadedPacks) {
      const manifest = AssetPackManager.getManifest(packId);
      const text = this.add.text(20, y, `✓ ${packId}${manifest ? ` - ${manifest.displayName}` : ''}`, {
        font: '12px monospace',
        color: '#4ade80',
      });
      container.add(text);
      y += 18;
    }

    if (loadedPacks.length === 0) {
      container.add(this.add.text(20, y, 'No packs loaded yet', {
        font: '12px monospace',
        color: '#666666',
      }));
    }

    // Pack index info
    const index = AssetPackManager.getIndex();
    if (index) {
      container.add(this.add.text(10, GAME_HEIGHT - 150, `Available: ${Object.keys(index.packs).length} packs`, {
        font: '12px Arial',
        color: '#888888',
      }));
    }
  }

  private selectAtlas(index: number): void {
    if (this.filteredAtlases.length === 0) return;

    this.currentAtlasIndex = (index + this.filteredAtlases.length) % this.filteredAtlases.length;
    const atlasKey = this.filteredAtlases[this.currentAtlasIndex];

    const texture = this.textures.get(atlasKey);
    this.frames = texture.getFrameNames().filter((f) => f !== '__BASE');
    this.filteredFrames = [...this.frames];
    this.frames.sort();

    this.currentFrameIndex = 0;
    this.updatePreview();
  }

  private nextAtlas(delta: number): void {
    this.selectAtlas(this.currentAtlasIndex + delta);
  }

  private nextFrame(delta: number): void {
    if (this.filteredFrames.length === 0) return;
    this.currentFrameIndex = (this.currentFrameIndex + delta + this.filteredFrames.length) % this.filteredFrames.length;
    this.updatePreview();
  }

  private updatePreview(): void {
    if (this.filteredAtlases.length === 0 || this.filteredFrames.length === 0) return;

    const atlasKey = this.filteredAtlases[this.currentAtlasIndex];
    const frameName = this.filteredFrames[this.currentFrameIndex];

    // Clean up old sprite
    this.previewSprite?.destroy();

    // Create new sprite centered
    this.previewSprite = this.add.sprite(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20, atlasKey, frameName);
    this.contentContainer?.add(this.previewSprite);

    // Apply pixel art mode
    if (this.pixelArtMode) {
      this.previewSprite.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
    } else {
      this.previewSprite.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    // Scale down if huge
    const maxDim = Math.max(this.previewSprite.width, this.previewSprite.height);
    if (maxDim > GAME_HEIGHT * 0.5) {
      const scale = (GAME_HEIGHT * 0.5) / maxDim;
      this.previewSprite.setScale(scale);
    }

    // Update info text
    this.infoText?.setText([
      `Atlas: ${atlasKey} (${this.currentAtlasIndex + 1}/${this.filteredAtlases.length})`,
      `Frame: ${frameName} (${this.currentFrameIndex + 1}/${this.filteredFrames.length})`,
      `Size: ${this.previewSprite.width} x ${this.previewSprite.height}`,
    ].join('\n'));
  }

  private updateAnimPreview(infoText: Phaser.GameObjects.Text): void {
    if (this.animKeys.length === 0) return;

    const animKey = this.animKeys[this.currentAnimIndex];
    const anim = this.anims.get(animKey);

    // Clean up old sprite
    this.animSprite?.destroy();

    if (anim && anim.frames.length > 0) {
      const firstFrame = anim.frames[0];
      this.animSprite = this.add.sprite(GAME_WIDTH / 2, GAME_HEIGHT / 2, firstFrame.textureKey, firstFrame.textureFrame);
      this.contentContainer?.add(this.animSprite);

      // Scale if needed
      const maxDim = Math.max(this.animSprite.width, this.animSprite.height);
      if (maxDim > GAME_HEIGHT * 0.4) {
        this.animSprite.setScale((GAME_HEIGHT * 0.4) / maxDim);
      }

      if (this.isAnimPlaying) {
        this.animSprite.play({ key: animKey, frameRate: this.animFps, repeat: -1 });
      }
    }

    infoText.setText([
      `Animation: ${animKey} (${this.currentAnimIndex + 1}/${this.animKeys.length})`,
      `Frames: ${anim?.frames.length || 0}`,
      `Default FPS: ${anim?.frameRate || 'N/A'}`,
    ].join('\n'));
  }

  private playAudio(key: string): void {
    // Stop current BGM
    if (this.currentBgm) {
      this.currentBgm.stop();
    }

    // Play new audio
    this.currentBgm = this.sound.add(key, { loop: true, volume: 0.5 });
    this.currentBgm.play();
    this.currentBgmKey = key;

    // Refresh audio tab
    if (this.currentTab === 'audio') {
      this.switchTab('audio');
    }
  }

  private togglePixelArt(): void {
    this.pixelArtMode = !this.pixelArtMode;

    // Refresh current view
    if (this.currentTab === 'textures') {
      this.updatePreview();
      this.switchTab('textures');
    }
  }

  private setupInputHandlers(): void {
    // ESC to close
    this.input.keyboard?.on('keydown-ESC', () => {
      this.scene.stop();
      this.scene.resume('WorldScene');
    });

    // TAB to switch tabs
    this.input.keyboard?.on('keydown-TAB', (event: KeyboardEvent) => {
      event.preventDefault();
      const tabs: Tab[] = ['textures', 'animations', 'audio', 'packs'];
      const currentIndex = tabs.indexOf(this.currentTab);
      const nextIndex = (currentIndex + 1) % tabs.length;
      this.switchTab(tabs[nextIndex]);
    });

    // P to toggle pixel art
    this.input.keyboard?.on('keydown-P', () => this.togglePixelArt());

    // Arrow keys for navigation
    this.input.keyboard?.on('keydown-RIGHT', () => {
      if (this.currentTab === 'textures') {
        this.nextFrame(1);
      } else if (this.currentTab === 'animations') {
        this.currentAnimIndex = (this.currentAnimIndex + 1) % this.animKeys.length;
        this.switchTab('animations');
      } else if (this.currentTab === 'audio') {
        this.currentAudioIndex = (this.currentAudioIndex + 1) % this.audioKeys.length;
      }
    });

    this.input.keyboard?.on('keydown-LEFT', () => {
      if (this.currentTab === 'textures') {
        this.nextFrame(-1);
      } else if (this.currentTab === 'animations') {
        this.currentAnimIndex = (this.currentAnimIndex - 1 + this.animKeys.length) % this.animKeys.length;
        this.switchTab('animations');
      } else if (this.currentTab === 'audio') {
        this.currentAudioIndex = (this.currentAudioIndex - 1 + this.audioKeys.length) % this.audioKeys.length;
      }
    });

    // PAGE UP/DOWN for atlas navigation
    this.input.keyboard?.on('keydown-PAGE_DOWN', () => {
      if (this.currentTab === 'textures') {
        this.nextAtlas(1);
      }
    });

    this.input.keyboard?.on('keydown-PAGE_UP', () => {
      if (this.currentTab === 'textures') {
        this.nextAtlas(-1);
      }
    });

    // UP/DOWN for FPS control in animations tab
    this.input.keyboard?.on('keydown-UP', () => {
      if (this.currentTab === 'animations') {
        this.animFps = Math.min(60, this.animFps + 1);
        this.switchTab('animations');
      }
    });

    this.input.keyboard?.on('keydown-DOWN', () => {
      if (this.currentTab === 'animations') {
        this.animFps = Math.max(1, this.animFps - 1);
        this.switchTab('animations');
      }
    });

    // SPACE for play/pause
    this.input.keyboard?.on('keydown-SPACE', (event: KeyboardEvent) => {
      event.preventDefault();
      if (this.currentTab === 'animations') {
        this.isAnimPlaying = !this.isAnimPlaying;
        this.switchTab('animations');
      } else if (this.currentTab === 'audio') {
        if (this.currentBgm?.isPlaying) {
          this.currentBgm.stop();
          this.currentBgmKey = undefined;
        } else if (this.audioKeys.length > 0) {
          this.playAudio(this.audioKeys[this.currentAudioIndex]);
        }
        this.switchTab('audio');
      }
    });

    // C to copy definition
    this.input.keyboard?.on('keydown-C', () => {
      if (this.currentTab === 'textures' && this.filteredAtlases.length > 0) {
        const atlasKey = this.filteredAtlases[this.currentAtlasIndex];
        const frameName = this.filteredFrames[this.currentFrameIndex];
        const def = { atlas: atlasKey, frame: frameName };
        console.log('Asset Definition:', JSON.stringify(def, null, 2));

        // Visual feedback
        const flash = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0xffffff, 0.2).setOrigin(0);
        this.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 200,
          onComplete: () => flash.destroy(),
        });
      }
    });
  }
}
